import React from 'react';
import { InferType, object } from 'yup';

import { GlobalAccount, GUID } from '@ndvr/customer-api';
import { Account, CustodyType } from '@ndvr/portfolio-api';

import { FormSheet } from '@/components/FormSheet';
import { useLinkAccountToCustodyAccount } from 'views/portfolio/account-queries';
import {
    custodyAccountIdValidator,
    FormInput,
    FormSelect,
    normalizeCustodyAccountId,
    requiredStringValidator,
    Ribbon,
    Text,
} from 'widgets';

const formSchema = object({
    custodyType: requiredStringValidator.oneOf(Object.values(CustodyType)),
    custodyAccountId: custodyAccountIdValidator('custodyType'),
});

type FormType = InferType<typeof formSchema>;

export const ChangeLinkedCustodyAccountDrawer = ({
    householdGUID,
    account,
    onClose,
}: {
    householdGUID: GUID<GlobalAccount>;
    account: Account;
    onClose: () => void;
}) => {
    const { mutate: linkCustodyAccount, isPending } = useLinkAccountToCustodyAccount(householdGUID);

    return (
        <FormSheet
            title={`Change linked custody account for ${account.accountName.plainText}`}
            onClose={onClose}
            formSchema={formSchema}
            pending={isPending}
            onSubmit={(formValues: FormType, close) => {
                linkCustodyAccount(
                    {
                        accountGroupGUID: account.accountGroupGUID,
                        accountGUID: account.accountGUID,
                        request: formValues,
                    },
                    { onSuccess: close },
                );
            }}
        >
            <FormSelect label="Custodian" name="custodyType">
                <option value={CustodyType.FIDELITY}>Fidelity</option>
                <option value={CustodyType.SCHWAB}>Charles Schwab</option>
                <option value={CustodyType.IB}>Interactive Brokers</option>
            </FormSelect>
            <FormInput
                label="Custody Account"
                name="custodyAccountId"
                placeholder="account id"
                normalize={normalizeCustodyAccountId}
            />
            <Ribbon intent="warning">
                <Text>
                    The linked custody account should only be changed if a transfer of assets is underway from the
                    current {account.custodyType} custody account {account.custodyAccountId} to another custody account.
                    Please let the engineering team know about this transfer, as an exclusion period needs to be added
                    for the transfer period to connect the reporting history of the two custody accounts.
                </Text>
            </Ribbon>
        </FormSheet>
    );
};
